/**
 * Type definitions for Secret Field Plugin
 */

/**
 * Secret field data structure
 */
export interface SecretField {
  /** Unique ID for React keys and state management */
  id: string;

  /** User-facing label (e.g., "API Token", "SSH Key") */
  label: string;

  /** Raw secret content */
  content: string;

  /** UI state - whether content is currently revealed (client-only, not persisted) */
  revealed: boolean;

  // Future properties (out of scope for this task):
  // encrypted?: boolean;
  // hint?: string;
  // encryptionMethod?: 'master' | 'field';
}

/**
 * Secret field format type (inline only)
 */
export type SecretFieldType = 'inline';

/**
 * Props for SecretFieldEditor component
 */
export interface SecretFieldEditorProps {
  /** Unique node key from Lexical */
  nodeKey: string;

  /** Field label */
  label: string;

  /** Secret content */
  content: string;

  /** Field type (always inline) */
  type: SecretFieldType;

  /** Callback when label or content is updated */
  onUpdate: (label: string, content: string) => void;

  /** Callback when editing is committed (blur/done) - for table cell save */
  onCommit?: () => void;

  /** Callback when field type is changed */
  onTypeChange?: (type: SecretFieldType) => void;

  /** Callback when field should be deleted */
  onDelete: () => void;
}

/**
 * Props for SecretFieldView component
 */
export interface SecretFieldViewProps {
  /** Unique node key from Lexical */
  nodeKey: string;

  /** Field label */
  label: string;

  /** Secret content */
  content: string;

  /** Field type (always inline) */
  type: SecretFieldType;

  /** Whether field is currently revealed */
  isRevealed: boolean;

  /** Callback to toggle revealed state */
  onToggle: () => void;
}

/**
 * Lexical node serialized JSON format
 */
export interface SecretFieldNodeJSON {
  type: 'secretField';
  label: string;
  content: string;
  fieldType: SecretFieldType;
  version: 1;
}

/**
 * Max content length constant
 */
export const MAX_SECRET_CONTENT_LENGTH = 2000;

/**
 * Validation result
 */
export interface ValidationResult {
  valid: boolean;
  error?: string;
}

